import React, { FC, useEffect, useRef } from "react";
import { useDataLayerPush } from "../hooks/useDataLayerPush";
import { stripPolicySearchParams } from "../utils/policySearchParams";

export interface DataLayerWrapperProps {
  children: any;
  data: any;
  serverData: any;
  location: {
    host: string;
    href: string;
  };
}

export const DataLayerWrapper: FC<DataLayerWrapperProps> = ({
  children,
  location,
}) => {
  const { dataLayerPush } = useDataLayerPush();

  const previousPath = useRef<string>();

  // Function to determine content type based on subdomain
  const getContentType = (url: string) => {
    try {
      const parsedUrl = new URL(url);
      const subdomain = parsedUrl.hostname.split(".")[0]; // Extract the subdomain
      return subdomain.includes("vet") ?
          "online claims - vet"
        : "online claims - customer";
    } catch {
      // Fallback if URL parsing fails
      return "online claims - customer";
    }
  };

  useEffect(() => {
    if (!location.host || !location.href) return;

    // Get referrer from localStorage or fallback to document.referrer
    const storedReferrer = localStorage.getItem("lastPage");
    const pageReferrer = stripPolicySearchParams(
      storedReferrer || previousPath.current || document.referrer,
    );

    // Store current page for next navigation
    localStorage.setItem("lastPage", stripPolicySearchParams(location.href));

    const pageData = {
      event: "virtual_pageview",
      spa_page_url: stripPolicySearchParams(location.href),
      spa_page_referrer: pageReferrer,
      content_group: "online claims",
      content_type: getContentType(location.href), // Dynamically set content type
    };

    // Push constructed data to data layer
    dataLayerPush({
      ...pageData,
    });

    // Update previous path ref for next navigation
    previousPath.current = location?.href;

    // Clean up only when leaving the site entirely
    const handleUnload = () => {
      localStorage.removeItem("lastPage");
    };

    window.addEventListener("beforeunload", handleUnload);
    return () => {
      window.removeEventListener("beforeunload", handleUnload);
    };
  }, [location, dataLayerPush]);

  return <>{children}</>;
};
